import { Map as ImmutableMap } from 'immutable';
import { FETCH_AGGREGATED_DEAL_ROLL_UPS_FAILED, FETCH_AGGREGATED_DEAL_ROLL_UPS_SUCCEEDED, FETCH_AGGREGATED_GOAL_BREAKDOWN, FETCH_AGGREGATED_GOALS_FAILED, FETCH_AGGREGATED_GOALS_SUCCEEDED, FETCH_AGGREGATED_SUBMISSIONS_FAILED, FETCH_AGGREGATED_SUBMISSIONS_SUCCEEDED, FETCH_OPEN_PIPELINE_VALUE_SUCCEEDED, TOGGLE_AGGREGATED_GOAL_BREAKDOWN_FETCHING, UPDATE_FORECAST_DISPLAY_VALUE } from '../actions/ActionTypes';
import { transformDealRollupsToUseCurrency, transformGoalsBreakdownToUseCurrency, transformGoalsPayloadToUseCurrency, transformSubmissionsPayloadToUseCurrency } from '../utils/aggregatedOverviewDataUtils';
const initialState = ImmutableMap({
  openPipelineValue: {
    currencyCode: '',
    value: 0
  },
  openPipelineValueFetched: false,
  dealRollups: ImmutableMap(),
  goals: ImmutableMap(),
  goalsBreakdown: ImmutableMap(),
  hasFetchSubmissionsError: false,
  hasFetchGoalsError: false,
  hasFetchDealRollupsError: false,
  isFetchingGoals: false,
  submissions: ImmutableMap()
});
export default function aggregatedOverviewData(state = initialState, action) {
  switch (action.type) {
    case FETCH_AGGREGATED_DEAL_ROLL_UPS_SUCCEEDED:
      {
        const {
          payload: {
            companyCurrency,
            data
          }
        } = action;
        if (!data) {
          return state;
        }
        return state.merge({
          dealRollups: transformDealRollupsToUseCurrency(data, companyCurrency)
        });
      }
    case FETCH_AGGREGATED_DEAL_ROLL_UPS_FAILED:
      {
        return state.merge({
          hasFetchDealRollupsError: true
        });
      }
    case FETCH_AGGREGATED_GOALS_FAILED:
      {
        return state.merge({
          hasFetchGoalsError: true
        });
      }
    case FETCH_AGGREGATED_SUBMISSIONS_FAILED:
      {
        return state.merge({
          hasFetchSubmissionsError: true
        });
      }
    case FETCH_OPEN_PIPELINE_VALUE_SUCCEEDED:
      {
        const {
          payload
        } = action;
        // Setting instead of merging to keep as Currency type
        // Instead of letting merge convert to Immutable Map
        return state.set('openPipelineValue', payload).set('openPipelineValueFetched', true);
      }
    case FETCH_AGGREGATED_GOALS_SUCCEEDED:
      {
        const {
          payload: {
            companyCurrency,
            data
          }
        } = action;
        if (!data) {
          return state;
        }
        return state.merge({
          goals: transformGoalsPayloadToUseCurrency(data, companyCurrency)
        });
      }
    case FETCH_AGGREGATED_SUBMISSIONS_SUCCEEDED:
      {
        const {
          payload: {
            companyCurrency,
            data
          }
        } = action;
        if (!data) {
          return state;
        }
        return state.merge({
          submissions: transformSubmissionsPayloadToUseCurrency(data, companyCurrency)
        });
      }
    case FETCH_AGGREGATED_GOAL_BREAKDOWN:
      {
        const {
          payload
        } = action;
        return state.merge({
          goalsBreakdown: transformGoalsBreakdownToUseCurrency(payload.data, payload.companyCurrency)
        });
      }
    case TOGGLE_AGGREGATED_GOAL_BREAKDOWN_FETCHING:
      {
        const {
          payload
        } = action;
        return state.merge({
          isFetchingGoals: payload
        });
      }
    case UPDATE_FORECAST_DISPLAY_VALUE:
      {
        const {
          payload
        } = action;
        const {
          amount,
          currentTeamId,
          id,
          isAllPipelinesSubmission,
          isFilteredToAllPipelines,
          isMultipipeline,
          isUser,
          prevAmount
        } = payload;
        const key = isUser ? 'displayedAggregationsByOwner' : 'displayedAggregationsByTeam';
        const attribute = isUser ? 'totalSubmissionAmount' : 'teamSubmissionAmount';
        const pathToForecast = ['submissions', key, String(id)];
        const pathToColumnTotal = ['submissions', 'totalForecastSubmission', 'total'];
        const pathToUserSubmissionTotal = ['submissions', 'totalForecastSubmission', 'userSubmissionTotal'];
        const hasAllPipelinesSubmission = state.getIn([...pathToForecast, 'isAllPipelines']);
        const isMultiButNotAllPipelines = isMultipipeline && !isFilteredToAllPipelines;

        // special case: total row team data
        // this displays under totalForecastSubmission not teamSubmissionAmount
        const hasTotalRowAllPipelinesSubmission = state.getIn(['submissions', 'totalForecastSubmission', 'isAllPipelines']);
        const isAllPipelinesWithNoAllPipelinesSubmission = isFilteredToAllPipelines && !hasAllPipelinesSubmission;

        // if the update is to a single pipeline, we're filtered to all pipelines,
        // and an all pipelines submission exists, do not instant update cell
        if ((hasAllPipelinesSubmission || hasTotalRowAllPipelinesSubmission) && !isAllPipelinesSubmission) {
          return state;
        }

        //if multiple pipelines are selected and submission is for a single pipeline, aggregate amount
        const aggregateAmountForMultiPipelines = isMultiButNotAllPipelines && !isAllPipelinesSubmission;

        //if all pipelines is selected, there is no all pipelines submission,
        // and the submission is for a single pipeline, aggregate amount
        const aggregateAmountForAllPipelines = isAllPipelinesWithNoAllPipelinesSubmission && !isAllPipelinesSubmission;
        const currentDisplayedSubmission = state.getIn([...pathToForecast, attribute]) || amount;
        const updatedAmount = aggregateAmountForMultiPipelines || aggregateAmountForAllPipelines ? amount.value - prevAmount.value + currentDisplayedSubmission.value : amount.value;

        // Update the user/team submission amount directly
        let newState = state.setIn([...pathToForecast, attribute], Object.assign({}, currentDisplayedSubmission, {
          value: updatedAmount
        })).mergeIn(pathToForecast, {
          lastModifiedDate: Date.now(),
          isAllPipelines: isAllPipelinesSubmission
        });

        // If it's a user amount, update the userSubmissionTotal as well
        if (isUser) {
          const userSubmissionTotal = state.getIn(pathToUserSubmissionTotal) || amount;
          newState = newState.setIn(pathToUserSubmissionTotal, Object.assign({}, userSubmissionTotal, {
            value: amount.value - prevAmount.value + userSubmissionTotal.value
          }));
        }

        // If its a team update and the team is the current total row, update the submission in the total row
        if (currentTeamId === id) {
          const totalSubmissionAmount = state.getIn([['submissions', 'totalForecastSubmission', attribute]]) || amount;
          newState = newState.setIn(['submissions', 'totalForecastSubmission', attribute], Object.assign({}, totalSubmissionAmount, {
            value: amount.value
          })).mergeIn(['submissions', 'totalForecastSubmission'], {
            lastModifiedDate: Date.now(),
            isAllPipelines: isAllPipelinesSubmission
          });
        } else {
          // Update total amount if we don't have an explict team submission at the total row
          const columnTotal = state.getIn(pathToColumnTotal);
          if (columnTotal) {
            newState = newState.setIn(pathToColumnTotal, Object.assign({}, columnTotal, {
              value: amount.value - prevAmount.value + columnTotal.value
            }));
          }
        }
        return newState;
      }
    default:
      return state;
  }
}